/**
 * useScrollFades Hook
 *
 * Manages scroll fade gradient visibility for horizontally scrollable containers.
 * Shows left/right fade gradients when content overflows and is scrolled.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollFadesResult {
  /** Ref to attach to the scrollable container */
  scrollRef: React.RefObject<HTMLDivElement>;
  /** Whether to show the left fade gradient */
  showLeftFade: boolean;
  /** Whether to show the right fade gradient */
  showRightFade: boolean;
  /** Manually trigger fade recalculation (e.g., after content changes) */
  updateFades: () => void;
}

/**
 * Hook for managing horizontal scroll fade indicators.
 *
 * @param deps - Dependencies that should trigger fade recalculation when changed
 * @returns Object with scrollRef, fade states, and manual update function
 *
 * @example
 * ```tsx
 * const { scrollRef, showLeftFade, showRightFade } = useScrollFades([items.length]);
 *
 * return (
 *   <div className="relative">
 *     {showLeftFade && <div className="fade-left" />}
 *     {showRightFade && <div className="fade-right" />}
 *     <div ref={scrollRef} className="overflow-x-auto">
 *       {items.map(...)}
 *     </div>
 *   </div>
 * );
 * ```
 */
export function useScrollFades(deps: React.DependencyList = []): ScrollFadesResult {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const hasOverflow = scrollWidth > clientWidth;

    setShowLeftFade(hasOverflow && scrollLeft > 0);
    setShowRightFade(hasOverflow && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateFades, { passive: true });
    window.addEventListener('resize', updateFades, { passive: true });

    return () => {
      el.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', updateFades);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFades, ...deps]);

  return { scrollRef, showLeftFade, showRightFade, updateFades };
}
